import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, TextField, Checkbox, Grid, InputLabel, Autocomplete } from '@mui/material';
import useStyles from './styles';
import {
  getDefaults,
  editDeviceTypeConfig,
} from '../../../store/admin/device-type-configuration/actions';
import {
  ConfigCapabilities,
  ConfigSupported,
} from '../../../store/admin/device-type-configuration/types';
import { AppState } from '../../../store';

const arrayEquals = (a: Array<string>, b: Array<string>) =>
  Array.isArray(a) &&
  Array.isArray(b) &&
  a.length === b.length &&
  a.every((value, index) => value === b[index]);

const ManageDeviceTypes: FC = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getDefaults());
  }, []);

  const classes = useStyles();
  const deviceTypes = useSelector((state: AppState) => state.deviceType.deviceTypes);
  const commandIds = useSelector((state: AppState) => state.deviceType.commandFileTypeIds);
  const responseIds = useSelector((state: AppState) => state.deviceType.responseFileTypeIds);
  const configNames = useSelector((state: AppState) => state.deviceType.configNames);
  const devicePool = useSelector((state: AppState) => state.deviceType.deviceTypePool);
  const [deviceTypeId, setDeviceTypeId] = useState<string>('');
  const [configName, setConfigName] = useState<string>('');
  const [isDeleteSupported, setIsDeleteSupported] = useState<boolean>(false);
  const [limit, setLimit] = useState<string>('');
  const [supportTUDR, setSupportTUDR] = useState<boolean>(false);
  const [commandFileIds, setComandFileIds] = useState<string[]>([]);
  const [responseFileIds, setResponseFileIds] = useState<string[]>([]);
  const [saveEnabled, setSaveEnabled] = useState<boolean>(false);
  const clearDependentFields = () => {
    setLimit('');
    setComandFileIds([]);
    setResponseFileIds([]);
    setIsDeleteSupported(false);
    setSupportTUDR(false);
  };
  const refreshState = () => {
    const parentRecord = devicePool.find((x) => x.deviceType === deviceTypeId);
    if (parentRecord?.configsSupported) {
      const childRecord = parentRecord.configsSupported.find((x) => x.config === configName);
      if (childRecord) {
        setLimit(childRecord.limit);
        setSupportTUDR(childRecord.supportsTudr);
        setComandFileIds(childRecord.commandFileTypeIds);
        setResponseFileIds(childRecord.responseFileTypeIds);
        setIsDeleteSupported(childRecord.supportsDelete);
      } else {
        clearDependentFields();
      }
    }
  };
  const saveDeviceConfig = () => {
    const newConfigNode = {
      limit,
      supportsTudr: supportTUDR,
      commandFileTypeIds: commandFileIds,
      responseFileTypeIds: responseFileIds,
      supportsDelete: isDeleteSupported,
      config: configName,
    };
    const deviceNode = devicePool.find((x: ConfigCapabilities) => x.deviceType === deviceTypeId);
    const deviceToUpload = {
      onboardApplicationMessageVersion: deviceNode?.onboardApplicationMessageVersion || 1,
      isDeleteConfigSupported: deviceNode?.isDeleteConfigSupported || true,
      configsSupported: deviceNode?.configsSupported || [],
    };
    if (deviceToUpload && deviceNode) {
      const configNodeIndex = deviceToUpload.configsSupported.findIndex(
        (x: ConfigSupported) => x.config === configName,
      );
      if (configNodeIndex >= 0) {
        deviceToUpload.configsSupported[configNodeIndex] = newConfigNode;
      } else {
        deviceToUpload.configsSupported.push(newConfigNode);
      }
      dispatch(editDeviceTypeConfig(deviceToUpload, deviceNode.deviceType));
    }
    setSaveEnabled(false);
  };

  useEffect(() => {
    if (deviceTypeId && configName) {
      refreshState();
    }
  }, [configName, deviceTypeId]);
  const deviceSelected = () => {
    if (deviceTypeId !== '' && configName !== '') {
      return true;
    }
    return false;
  };
  const validLimit =
    limit !== '' && !(`${Math.abs(Number.parseInt(limit, 10))}` === limit || limit === 'N/A');

  const isSaveEnabled = () => {
    if (!deviceSelected()) {
      return false;
    }
    const parentRecord = devicePool.find((x) => x.deviceType === deviceTypeId);
    if (parentRecord?.configsSupported) {
      const childRecord = parentRecord.configsSupported.find((x) => x.config === configName);
      if (
        childRecord &&
        limit === childRecord.limit &&
        !validLimit &&
        supportTUDR === childRecord.supportsTudr &&
        arrayEquals(commandFileIds, childRecord.commandFileTypeIds) &&
        arrayEquals(responseFileIds, childRecord.responseFileTypeIds) &&
        isDeleteSupported === childRecord.supportsDelete
      ) {
        return false;
      }
      if (
        (limit !== '' ||
          supportTUDR !== false ||
          !arrayEquals(commandFileIds, []) ||
          !arrayEquals(responseFileIds, []) ||
          isDeleteSupported !== false) &&
        !validLimit
      ) {
        return true;
      }
    }
    return false;
  };

  const boxProperties = {
    p: 3,
    className: classes.box,
  };
  useEffect(() => {
    setSaveEnabled(isSaveEnabled());
  }, [commandFileIds, responseFileIds, isDeleteSupported, supportTUDR, limit]);

  return (
    <>
      <Grid container direction="row" alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Box {...boxProperties}>
            <InputLabel>Device Type</InputLabel>
            <Autocomplete
              id="deviceType"
              options={deviceTypes}
              className={classes.dropdown}
              renderInput={(parameters) => <TextField variant="standard" {...parameters} />}
              onChange={(event, newValue) => {
                if (newValue) {
                  setDeviceTypeId(newValue);
                } else {
                  clearDependentFields();
                }
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box {...boxProperties}>
            <InputLabel>Config Name</InputLabel>
            <Autocomplete
              id="configName"
              options={configNames}
              className={classes.dropdown}
              renderInput={(parameters) => <TextField variant="standard" {...parameters} />}
              onChange={(event, newValue) => {
                if (newValue) {
                  setConfigName(newValue);
                } else {
                  clearDependentFields();
                }
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} lg={6} className={classes.multigrid}>
          <Box p={3} className={classes.multibox}>
            <InputLabel>Command File Type IDs</InputLabel>
            <Autocomplete
              multiple
              disabled={!deviceSelected()}
              id="commandFileTypes"
              options={commandIds}
              value={commandFileIds}
              onChange={(event: any, newValue: string[] | []) => setComandFileIds(newValue)}
              className={classes.multicomplete}
              renderInput={(parameters) => <TextField {...parameters} variant="standard" />}
            />
          </Box>
        </Grid>
        <Grid item xs={12} lg={6} className={classes.multigrid}>
          <Box p={3} className={classes.multibox}>
            <InputLabel>Response File Type IDs</InputLabel>
            <Autocomplete
              multiple
              disabled={!deviceSelected()}
              id="responseFileTypes"
              options={responseIds}
              onChange={(event: any, newValue: string[] | []) => setResponseFileIds(newValue)}
              value={responseFileIds}
              className={classes.multicomplete}
              renderInput={(parameters) => <TextField {...parameters} variant="standard" />}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box {...boxProperties}>
            <InputLabel>Is Delete Config Supported?</InputLabel>
            <Checkbox
              disabled={!deviceSelected()}
              className={classes.checkbox}
              checked={isDeleteSupported}
              onChange={(event) => setIsDeleteSupported(event.target.checked as boolean)}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box {...boxProperties}>
            <InputLabel>Limit</InputLabel>
            <TextField
              variant="standard"
              id="limit"
              disabled={!deviceSelected()}
              className={classes.textfield}
              value={limit}
              onChange={(event) => setLimit(event.target.value as string)}
              error={validLimit}
              helperText={validLimit && 'Positive integers or N/A only'}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box {...boxProperties}>
            <InputLabel>Support TUDR?</InputLabel>
            <Checkbox
              disabled={!deviceSelected()}
              className={classes.checkbox}
              checked={supportTUDR}
              onChange={(event) => setSupportTUDR(event.target.checked as boolean)}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box {...boxProperties}>
            <Button
              disabled={!saveEnabled}
              className={classes.button}
              variant="outlined"
              onClick={() => {
                saveDeviceConfig();
              }}
            >
              Save
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default ManageDeviceTypes;
